import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, FileUp, Loader2, Sparkles, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-violet transition-colors";

const TYPE_META = {
  priority: ["Prioridad", "bg-violet/10 text-violet"],
  dependency: ["Dependencia", "bg-blue-50 text-blue-700"],
  resource_allocation: ["Asignación de recursos", "bg-emerald-50 text-emerald-700"],
  rebalancing: ["Rebalanceo", "bg-amber-50 text-amber-700"],
  productivity: ["Productividad", "bg-cyan-50 text-cyan-700"],
  buffer: ["Buffer", "bg-orange-50 text-orange-700"],
  calendar: ["Calendario", "bg-pink-50 text-pink-700"],
  constraint: ["Restricción", "bg-navy/5 text-navy/60"],
};

const TypeBadge = ({ type }) => {
  const [l, cls] = TYPE_META[type] || [type, "bg-navy/5 text-navy/60"];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cls}`}>{l}</span>;
};

const EXAMPLE = `Los postes se priorizan por mayor cantidad.
El tendido comienza con una cuadrilla en CABA y dos en Provincia.
Cuando CABA termine, las tres cuadrillas de tendido deben trabajar sobre Provincia.
Las puestas a tierra se priorizan por mayor peso.
La auditoría solo puede comenzar cuando cambio de postes, tendido y puesta a tierra estén terminados.
Reservar un día hábil antes del cierre para correcciones.`;

export default function RulesTab({ plan, onChange }) {
  const [text, setText] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [proposed, setProposed] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const docRef = useRef(null);

  const interpret = async () => {
    setInterpreting(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/rules/interpret`, { text });
      setProposed(data.interpreted);
      setConflicts(data.conflicts || []);
      if (!data.interpreted.length) toast.info("La IA no encontró reglas que interpretar");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al interpretar"); }
    finally { setInterpreting(false); }
  };

  const fromDoc = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setInterpreting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/planning/plans/${plan.id}/rules/from-doc`, fd);
      setProposed(data.interpreted);
      setConflicts(data.conflicts || []);
      if (!data.interpreted.length) toast.info("No encontré reglas en el documento");
      else toast.success(`${data.interpreted.length} regla(s) interpretadas de "${f.name}"`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error al leer el documento"); }
    finally { setInterpreting(false); }
  };

  const reject = (id) => setProposed((prev) => prev.filter((r) => r.id !== id));

  const confirmAll = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/rules/confirm`, { rules: proposed });
      toast.success(`${data.confirmed} regla(s) confirmadas`);
      setConflicts(data.conflicts || []);
      setProposed([]); setText(""); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al confirmar"); }
    finally { setConfirming(false); }
  };

  const delRule = async (r) => {
    if (!window.confirm(`¿Eliminar la regla "${r.original || r.resumen}"?`)) return;
    try { await api.delete(`/planning/plans/${plan.id}/rules/${r.id}`); onChange(); } catch { toast.error("Error"); }
  };

  const confirmed = plan.rules || [];

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="rules-tab">
      <div className="space-y-3">
        <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3">
          <div className="overline text-navy/40">Describe cómo debe planificarse este trabajo…</div>
          <textarea className={`${inputCls} min-h-[180px]`} value={text} data-testid="rules-textarea"
            placeholder={"Escribe tus instrucciones en lenguaje natural, una por línea.\n\nEj.: " + EXAMPLE.split("\n")[0]}
            onChange={(e) => setText(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button onClick={interpret} disabled={interpreting || !text.trim()} data-testid="interpret-btn"
              className="rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
              {interpreting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Interpretar reglas
            </button>
            <input ref={docRef} type="file" accept=".docx,.pdf,.txt" className="hidden" data-testid="rules-doc-input" onChange={fromDoc} />
            <button onClick={() => docRef.current?.click()} disabled={interpreting} data-testid="rules-doc-btn"
              className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet inline-flex items-center gap-2 disabled:opacity-50">
              <FileUp className="w-4 h-4" /> Subir Word / PDF
            </button>
            {plan.template === "infraestructura" && (
              <button onClick={() => setText(EXAMPLE)} data-testid="example-rules-btn"
                className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet">Usar reglas del caso de prueba</button>
            )}
          </div>
          <p className="text-[11px] text-navy/35">OMNIA AI interpreta; el motor determinístico calcula. Ninguna regla se aplica sin tu confirmación.</p>
        </div>

        {conflicts.length > 0 && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-1.5" data-testid="conflicts-panel">
            <div className="flex items-center gap-2 text-red-700 font-medium text-sm"><AlertTriangle className="w-4 h-4" /> Conflictos detectados</div>
            {conflicts.map((c, i) => <p key={i} className="text-xs text-red-600">{c.detalle}</p>)}
          </div>
        )}

        {proposed.length > 0 && (
          <div className="rounded-xl border border-violet/30 bg-violet/[0.03] p-5 space-y-3" data-testid="proposed-rules">
            <div className="flex items-center gap-2">
              <div className="overline text-navy/50">OMNIA interpretó {proposed.length} regla(s)</div>
              <button onClick={confirmAll} disabled={confirming} data-testid="confirm-all-btn"
                className="ml-auto rounded-full bg-navy text-cream text-xs px-4 py-2 hover:bg-navy-soft disabled:opacity-50 inline-flex items-center gap-1.5">
                {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirmar todas
              </button>
            </div>
            {proposed.map((r) => (
              <div key={r.id} className="rounded-lg border border-navy/10 bg-white p-3.5 space-y-1.5" data-testid={`proposed-rule-${r.id}`}>
                <div className="flex items-center gap-2">
                  <TypeBadge type={r.type} />
                  {r.status === "needs_review" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Revisar</span>}
                  <button onClick={() => reject(r.id)} title="Rechazar" className="ml-auto text-navy/25 hover:text-destructive"><X className="w-4 h-4" /></button>
                </div>
                <p className="text-sm italic text-navy/60">"{r.original}"</p>
                <p className="text-sm">{r.resumen}</p>
                <pre className="text-[10px] text-navy/40 bg-navy/[0.03] rounded p-2 overflow-x-auto">{JSON.stringify(r.params)}</pre>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3">
        <div className="overline text-navy/40">Reglas confirmadas ({confirmed.length})</div>
        {confirmed.length === 0 ? (
          <p className="text-sm text-navy/40">Aún no hay reglas. Descríbelas a la izquierda y confírmalas; el Planning Engine las aplicará al generar el plan.</p>
        ) : confirmed.map((r) => (
          <div key={r.id} className="rounded-lg border border-navy/10 p-3.5 space-y-1" data-testid={`confirmed-rule-${r.id}`}>
            <div className="flex items-center gap-2">
              <TypeBadge type={r.type} />
              <span className="text-[10px] text-navy/35">{r.confirmed_by}</span>
              <button onClick={() => delRule(r)} className="ml-auto text-navy/25 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {r.original && <p className="text-sm italic text-navy/60">"{r.original}"</p>}
            <p className="text-xs text-navy/50">{r.resumen}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
